// DEPENDENCIES ---------------------------------------------------------------- //

import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { Container, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import { BiSolidReceipt, BiSolidSpreadsheet } from 'react-icons/bi';

import { useCustomerConfig, useCustomerConfigLoaded, useRooftop, useUser } from '@store';
import { PROGRAM_PAGE_ACCESS, resolveProgramPageAccess } from '@features/programs/programVisuals';
import { canViewProgramReports } from '@features/programs/programReports';
import ProgramClaimsSection from '@features/programs/components/ProgramClaimsSection';
import ProgramReportsSection from '@features/programs/components/ProgramReportsSection';

// COMPONENT ---------------------------------------------------------------- //

const PROGRAM_TAB = { CLAIMS: 'claims', REPORTS: 'reports' } as const;
type ProgramTabId = (typeof PROGRAM_TAB)[keyof typeof PROGRAM_TAB];

/**
 * Program Management — dealer-facing program surfaces, tabbed.
 *
 * Claims & Ledger is this customer's own coverage and is shown to anyone who reaches the page.
 * Reports is program-wide detail for staff, so the tab is built only when
 * `canViewProgramReports` allows it — dealers and dealer admins never see it exists.
 *
 * The whole page is gated on the customer's `program_visuals` flag; a customer not in a program
 * never gets here at all.
 */
const ProgramsPage = () => {
  const rooftop = useRooftop();
  const user = useUser();
  const customerConfig = useCustomerConfig();

  const customerId = rooftop?.id ?? user?.profile?.customer_id ?? 0;
  const customerConfigLoaded = useCustomerConfigLoaded();
  const access = resolveProgramPageAccess(customerConfigLoaded, customerConfig);

  const [activeTab, setActiveTab] = useState(0);

  const tabs = useMemo(() => {
    const available: Array<{ id: ProgramTabId; label: string; icon: React.ReactElement }> = [
      { id: PROGRAM_TAB.CLAIMS, label: 'Claims & Ledger', icon: <BiSolidReceipt size={16} /> },
    ];
    if (canViewProgramReports(user)) {
      available.push({ id: PROGRAM_TAB.REPORTS, label: 'Reports', icon: <BiSolidSpreadsheet size={16} /> });
    }
    return available;
  }, [user]);

  // Indexes drive the MUI Tabs value, but the id decides what renders, so losing the Reports tab
  // can never leave a stale index pointing at a tab the caller is not allowed to see.
  const activeTabId = tabs[activeTab]?.id ?? PROGRAM_TAB.CLAIMS;

  if (access === PROGRAM_PAGE_ACCESS.LOADING) return <LinearProgress />;
  // Not in a program → the surface never shows.
  if (access === PROGRAM_PAGE_ACCESS.REDIRECT) return <Navigate to='/home' replace />;

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      <Typography variant='h3' sx={{ fontWeight: 600, mb: 1 }}>
        Program Management
      </Typography>

      {/* A lone tab is noise, so the strip only appears once Reports is available. */}
      {tabs.length > 1 && (
        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {tabs.map(tab => (
            <Tab
              key={tab.id}
              icon={tab.icon}
              iconPosition='start'
              label={tab.label}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
          ))}
        </Tabs>
      )}

      {activeTabId === PROGRAM_TAB.REPORTS ? (
        <ProgramReportsSection />
      ) : (
        <>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Your vehicles' program eligibility and the expected payments and reimbursements. Amounts marked{' '}
            <strong>*</strong> are estimates until the move is scheduled and the program confirms coverage.
          </Typography>
          <ProgramClaimsSection customerId={customerId} role={user?.profile?.default_role} />
        </>
      )}
    </Container>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default ProgramsPage;
